import React, { FC, Fragment, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { getDefaultDisplayDate } from 'src/app/common/utils';
import { PaginateListMore } from 'src/app/common/types';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { FormMode, RulesListItem, RulesStatus, RulesType } from 'src/app/modules/PulseLeads/types/auto-lead-reassignment-types';
import { autoLeadReassignmentStatusRulePath, autoLeadReassignmentTerminatedAgentRulePath } from '../../../AutoLeadReassignmentRoutes';
import { Button, Dialog } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { checkLeadsBeforePublish, updateStatus } from 'src/app/modules/PulseLeads/network/autoLeadReassignmentCrud';
import { useDispatch } from 'react-redux';

type TypeProps = {
  isLoading: boolean;
  dataList?: PaginateListMore<RulesListItem>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  ruleType: RulesType;
};

const useStyles = makeStyles()((theme) => ({
  dialogContainer: {
    padding: '20px',
  },
  btnContainer: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));


function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const RulesList: FC<TypeProps> = ({
  isLoading,
  dataList,
  onRefresh,
  onChangePage,
  ruleType
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [ruleSelected, setRuleSelected] = useState<Record<string, any> | null>(null);

  const handlePopupAction = async (action: string, id: string, status: RulesStatus) => {
    if (status === RulesStatus.active) {
      const responseCheck = await checkLeadsBeforePublish(ruleType, id, dispatch);
      setRuleSelected({
        id,
        action,
        status,
        totalLeads: responseCheck.totalLeads,
        limitLeads: responseCheck.limitLeads,
        isWithinLimit: responseCheck.totalLeads <= responseCheck.limitLeads,
      });
    } else {
      setRuleSelected({ id, action, status });
    }
  }
  const confirmActionOnRuleSelected = async () => {
    if (ruleSelected) {
      try {
        await updateStatus(ruleType, ruleSelected.id, ruleSelected.status, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: `${capitalizeFirstLetter(ruleSelected.action)} successfully`,
            },
          ]),
        );
        setRuleSelected(null);
        onRefresh();
      } catch (err: any) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: `${capitalizeFirstLetter(ruleSelected.action)} failed`,
            },
          ]),
        );
        setRuleSelected(null);
        onRefresh();
      }
    }
  };

  return (
    <Fragment>
      <PruTable
        title={"Rule List"}
        disableBulkSelect
        operationDef={[
          {
            title: 'View',
            tooltipText: 'View',
            onClick: (row) => history.push(
              ruleType === RulesType.statusRule
                ? `${autoLeadReassignmentStatusRulePath}/view/${row._id}`
                : `${autoLeadReassignmentTerminatedAgentRulePath}/view/${row._id}`,
              { type: FormMode.VIEW }
            ),
          },
          {
            title: 'Publish',
            tooltipText: 'Publish',
            onClick: (row) => handlePopupAction('Publish', row._id, RulesStatus.active),
            condition: (row) => row.status === RulesStatus.stopped,
          },
          {
            title: 'Stop',
            tooltipText: 'Stop',
            onClick: (row) => handlePopupAction('Stop', row._id, RulesStatus.stopped),
            condition: (row) => row.status === RulesStatus.active,
          },
          {
            title: 'Lead List',
            tooltipText: 'Lead List',
            onClick: (row) => history.push(
              ruleType === RulesType.statusRule
                ? `${autoLeadReassignmentStatusRulePath}/${row._id}/lead-list`
                : `${autoLeadReassignmentTerminatedAgentRulePath}/${row._id}/lead-list`
            ),
          },
        ]}
        headerBtnDef={[
          {
            color: 'primary',
            title: "Create New",
            onClick: () => history.push(
              ruleType === RulesType.statusRule
                ? `${autoLeadReassignmentStatusRulePath}/create`
                : `${autoLeadReassignmentTerminatedAgentRulePath}/create`,
              { type: FormMode.CREATE }
            ),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: (row) => '',
          },
          {
            keyIndex: 'ruleId',
            displayName: 'Rule ID',
            renderData: (row) => row.ruleId || '-',
          },
          {
            keyIndex: 'campaignFrom',
            align: 'left',
            displayName: 'From campaign',
            renderData: (row) => row?.campaignFrom?.campaignName ? String(row?.campaignFrom?.campaignName) : '-',
          },
          {
            keyIndex: 'campaignTo',
            align: 'left',
            displayName: 'To campaign',
            renderData: (row) => row?.campaignTo?.campaignName ? String(row?.campaignTo?.campaignName) : '-',
          },
          {
            keyIndex: 'status',
            align: 'center',
            displayName: 'Status',
            renderData: (row) => row.status || '-',
          },
          {
            keyIndex: 'publishedAt',
            align: 'center',
            displayName: 'Publish time',
            renderData: (row) => row.publishedAt && row.status !== RulesStatus.stopped ? getDefaultDisplayDate(new Date(row.publishedAt), 'datetime') : '-',
          },
          {
            keyIndex: 'stoppedAt',
            align: 'center',
            displayName: 'Stop time',
            renderData: (row) => row.stoppedAt && row.status === RulesStatus.stopped ? getDefaultDisplayDate(new Date(row.stoppedAt), 'datetime') : '-',
          },
          {
            keyIndex: 'createdBy',
            align: 'left',
            displayName: 'Create by',
            renderData: (row) => row.createdBy || '-',
          },
          {
            keyIndex: 'lastUpdatedBy',
            align: 'left',
            displayName: 'Last updated by',
            renderData: (row) => row.lastUpdatedBy || '-',
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={dataList?.items}
        totalPages={dataList?.totalPages}
        totalRecords={dataList?.totalRecords}
        onChangePage={onChangePage}
      />

      {ruleSelected && (
        <Dialog open={!!ruleSelected} onClose={() => setRuleSelected(null)}>
          <div className={classes.dialogContainer}>{
            ruleSelected?.action === 'Publish'
              ? ruleSelected.isWithinLimit
                ? `Rule is set. There will be ${ruleSelected?.totalLeads || 0} leads (at this moment) that are going to be reassigned. Are you sure you want to publish the rule?`
                : `The total of lead that are going to be reassigned is more than ${ruleSelected?.limitLeads || 0} (Actual number: ${ruleSelected?.totalLeads || 0}). Please adjust your setting.`
              : `Are you sure you want to ${ruleSelected?.action ?? ''} it?`
          }</div>
          <div className={classes.btnContainer}>
            {
              ruleSelected.isWithinLimit || ruleSelected.action !== 'Publish'
                ?
                <>
                  <Button
                    style={{ marginRight: 20 }}
                    variant="contained"
                    color="secondary"
                    onClick={() => confirmActionOnRuleSelected()}
                  >
                    {Translation('global.text.yes')}
                  </Button>
                  <Button variant="contained" color="inherit" onClick={() => setRuleSelected(null)}>
                    {Translation('global.text.no')}
                  </Button>
                </>
                :
                <Button variant="contained" color="inherit" onClick={() => setRuleSelected(null)}>
                  {Translation('global.text.cancel')}
                </Button>
            }
          </div>
        </Dialog>
      )}
    </Fragment>
  );
};

export default RulesList;
